// src/components/Login.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import {
  apiFetch,
  setAuthToken,
  setKeepSignedIn,
  AUTH_USER_KEY,
} from '../utils/api';


const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedInChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const loginUrl = `${API_BASE_URL}/api/login`;
    console.log('Sending login request to:', loginUrl, {
      payload: { email: email.trim(), keepSignedIn },
      timestamp: new Date().toISOString(),
    });
    try {
      const response = await apiFetch(loginUrl, {
        skipAuth: true,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          keepSignedIn,
        }),
      });
      console.log('Login response:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        redirected: response.redirected,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.log('Login error response:', data);
        if (response.status === 404) {
          throw new Error('Login endpoint not found. Please contact the administrator.');
        } else if (response.status === 401) {
          throw new Error('Invalid email or password');
        } else if (response.status === 400) {
          throw new Error(data.error || `Bad request: ${response.status}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const { token, user } = data;
      setAuthToken(token);
      setKeepSignedIn(!!data.keepSignedIn || keepSignedIn);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      console.log('Login successful:', {
        email,
        role: user.role,
        keepSignedIn: !!data.keepSignedIn || keepSignedIn,
      });
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during login');
    }
  };

  const handleForgotPassword = () => {
    console.log('Forgot Password clicked for email:', email);
    setError('Ask a SuperAdmin to reset your password (Management → Users → Reset password).');
  };

  return (
    <div className="page-container" style={{ maxWidth: '400px', margin: '0 auto', paddingTop: '100px' }}>
      <h2 style={{ color: '#EEC930', textAlign: 'center', marginBottom: '20px' }}>Login</h2>
      {error && <div className="error" style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}
      <form onSubmit={handleLogin}>
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              autoComplete="username"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #CCCCCC',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box',
                color: '#000000',
                backgroundColor: '#FFFFFF',
              }}
              required
            />
          </div>
          <div>
            <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>Password:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #CCCCCC',
                borderRadius: '4px',
                fontSize: '16px',
                boxSizing: 'border-box',
                color: '#000000',
                backgroundColor: '#FFFFFF',
              }}
              required
            />
          </div>
          <label
            htmlFor="keep-signed-in"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              color: '#555',
              fontSize: '15px',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              id="keep-signed-in"
              data-testid="keep-signed-in"
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedInChecked(e.target.checked)}
              style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }}
            />
            <span>
              <span style={{ fontWeight: 'bold', display: 'block' }}>Keep me signed in</span>
              <span style={{ fontSize: '13px', color: '#777' }}>
                Stay signed in on this device for about 30 days (until you log off). Use on phones you trust.
              </span>
            </span>
          </label>
          <button
            type="submit"
            style={{
              backgroundColor: '#2196F3',
              color: '#fff',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#1976D2')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#2196F3')}
          >
            Login
          </button>
          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{ color: '#0066CC', textDecoration: 'underline', fontSize: '14px', background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseOver={(e) => (e.currentTarget.style.color = '#F86752')}
              onMouseOut={(e) => (e.currentTarget.style.color = '#0066CC')}
            >
              Forgot Password?
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Login;
